import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { EntityDialogService } from '../../../../../../services/entity-dialog.service';
import { ToastService } from '../../../../../../services/toast.service';
import { CampaignModelStore } from '../../../../../../stores/campaign-model.store';
import { CampaignModel } from '../../../../../../types/campaign.interface';
import { EntityDefinition } from '../../../../../../types/entity-definition.interface';
import { isEntityFormDeleteAction } from '../../../../../../types/entity-form-result.interface';

const MODEL_NAME_MAX_LENGTH = 100;

// A model only owns its name; its planning lives on the snapshot campaign and is
// managed from the model detail page.
const RENAME_DEFINITION: EntityDefinition<CampaignModel> = {
  entityType: 'CampaignModel',
  list: { columns: [] },
  fields: [
    {
      field: 'name',
      labelKey: 'entities.campaign.models.name',
      required: true,
      type: 'text',
      minLength: 3,
      maxLength: MODEL_NAME_MAX_LENGTH,
    },
  ],
  messages: {
    dialogTitles: {
      create: () => 'common.buttons.add',
      edit: () => 'entities.campaign.models.editTooltip',
      delete: () => 'entities.campaign.models.deleteTooltip',
    },
    buttons: {
      add: () => 'common.buttons.add',
      create: () => 'common.buttons.add',
      edit: () => 'common.buttons.edit',
      delete: () => 'common.buttons.delete',
    },
  },
};

@Injectable({ providedIn: 'root' })
export class CampaignModelActionsService {
  private readonly dialogService = inject(EntityDialogService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly toastService = inject(ToastService);
  private readonly campaignModelStore = inject(CampaignModelStore);
  private readonly translate = inject(TranslocoService);

  // Inline uniqueness like tables/graphs: ces-text-field flags a duplicate name (disables submit
  // + shows `validation.unique`) against the asset's loaded models. The server 409 is the hard guard.
  private uniqueNameDefinition(excludeName?: string): EntityDefinition<CampaignModel> {
    const models = this.campaignModelStore.entities();
    return {
      ...RENAME_DEFINITION,
      fields: (RENAME_DEFINITION.fields ?? []).map(field =>
        field.field === 'name'
          ? {
              ...field,
              unicityArray: models,
              unicityKey: 'name',
              ...(excludeName !== undefined ? { unicityExcludeValue: excludeName } : {}),
            }
          : field,
      ),
    };
  }

  /** Opens the rename dialog; resolves true when the model was updated. */
  async rename(model: CampaignModel): Promise<boolean> {
    const result = await lastValueFrom(
      this.dialogService.openEditDialog<CampaignModel>(model, {
        header: this.translate.translate('entities.campaign.models.editTooltip'),
        entityDefinition: this.uniqueNameDefinition(model.name),
        mode: 'edit',
      }),
    );
    if (!result || isEntityFormDeleteAction(result)) return false;

    try {
      await this.campaignModelStore.update(model.id, result);
      this.toastService.success('common.toasts.updateSuccess');
      return true;
    } catch (err) {
      this.notifyMutationError(err);
      return false;
    }
  }

  // The server enforces name uniqueness (409); surface a precise message instead of a
  // generic failure so the user understands the rejection even if the inline check is bypassed.
  private notifyMutationError(err: unknown): void {
    const status = (err as { status?: number } | null)?.status;
    this.toastService.error(status === 409 ? 'entities.campaign.models.duplicateError' : 'common.toasts.operationFailed');
  }

  /**
   * Opens the creation dialog and creates an empty model (name only); resolves to it so
   * the caller can navigate to its detail page to plan measures, mirroring tables/graphs.
   */
  async create(assetId: number): Promise<CampaignModel | null> {
    const result = await lastValueFrom(
      this.dialogService.openCreateDialog<CampaignModel>({
        header: this.translate.translate('entities.campaign.models.createTitle'),
        entityDefinition: this.uniqueNameDefinition(),
        mode: 'create',
      }),
    );
    if (!result || isEntityFormDeleteAction(result)) return null;
    const name = (result as Partial<CampaignModel>).name;
    if (!name) return null;

    try {
      return await this.campaignModelStore.createModel(assetId, name);
    } catch (err) {
      this.notifyMutationError(err);
      return null;
    }
  }

  /**
   * Opens the name dialog pre-filled with a non-colliding "{name} - Copie", then
   * deep-copies the model server-side. Resolves to the created copy (or null when
   * cancelled/failed) so the caller can navigate to it, mirroring tables/graphs.
   */
  async duplicate(model: CampaignModel, siblings: readonly string[]): Promise<CampaignModel | null> {
    const baseName = model.name ?? '';
    const proposedName = this.proposedCopyName(baseName, siblings);
    const result = await lastValueFrom(
      this.dialogService.openEditDialog<CampaignModel>(
        { ...model, name: proposedName },
        {
          header: this.translate.translate('entities.campaign.models.duplicateTitle', { name: baseName }),
          entityDefinition: this.uniqueNameDefinition(),
          mode: 'create',
        },
      ),
    );
    if (!result || isEntityFormDeleteAction(result)) return null;

    try {
      const copy = await this.campaignModelStore.duplicate(model.id);
      const targetName = (result as Partial<CampaignModel>).name;
      if (copy && targetName && copy.name !== targetName) {
        await this.campaignModelStore.update(copy.id, { name: targetName });
        return { ...copy, name: targetName };
      }
      return copy;
    } catch {
      this.toastService.error('common.toasts.operationFailed');
      return null;
    }
  }

  // Default copy name mirrors the backend suffix " - Copie" (capped at the 100-char
  // model-name limit) and disambiguates against sibling names so the dialog never
  // opens onto a name the user would immediately have to fix.
  private proposedCopyName(baseName: string, siblings: readonly string[]): string {
    const suffix = ` - ${this.translate.translate('common.labels.copySuffix')}`;
    const trimmedBase =
      baseName.length > MODEL_NAME_MAX_LENGTH - suffix.length ? baseName.slice(0, MODEL_NAME_MAX_LENGTH - suffix.length) : baseName;
    const base = `${trimmedBase}${suffix}`;
    const taken = new Set(siblings);
    if (!taken.has(base)) return base;
    for (let n = 2; n < 100; n++) {
      const candidate = `${base} (${n})`;
      if (!taken.has(candidate)) return candidate;
    }
    return base;
  }

  /** Confirms then deletes the model; resolves true when the deletion went through. */
  async delete(model: CampaignModel): Promise<boolean> {
    let deleted = false;
    await this.confirmationHelper.confirmAndDelete({
      entityName: model.name ?? '',
      messageKey: 'entities.campaign.models.deleteConfirm',
      successKey: 'common.toasts.deleteSuccess',
      onDelete: async () => {
        await this.campaignModelStore.delete(model.id);
        deleted = true;
      },
    });
    return deleted;
  }
}
